import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useClient } from '../../context/ClientContext';
import api from '../../services/api';
import { Client } from '../../types';
import DashboardHeader from '../dashboard/DashboardHeader';
import Footer from '../dashboard/Footer';
import './OptedListPage.css';

export interface OptInRow {
  id: number;
  value: string;
  joined_date: string;
}

interface OptedListPageProps {
  type: 'email' | 'phone';
}

const OptedListPage: React.FC<OptedListPageProps> = ({ type }) => {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { activeClients } = useClient();
  const [clients, setClients] = useState<Client[]>([]);
  const [clientId, setClientId] = useState<number | null>(null);
  const [rows, setRows] = useState<OptInRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const isAdminUser = isAdmin();
  const clientList = isAdminUser ? clients : activeClients;

  useEffect(() => {
    if (isAdminUser) {
      api.get<Client[]>('/clients').then((res) => {
        setClients(res.data);
        if (res.data.length > 0 && clientId === null) {
          setClientId(res.data[0].id);
        }
      }).catch(() => setClients([]));
    }
  }, [isAdminUser]);

  useEffect(() => {
    if (!isAdminUser && activeClients.length > 0 && clientId === null) {
      setClientId(activeClients[0].id);
    }
  }, [isAdminUser, activeClients, clientId]);

  useEffect(() => {
    if (!clientId) {
      setRows([]);
      setListLoading(false);
      setLoading(false);
      return;
    }
    setListLoading(true);
    api.get<OptInRow[]>('/opt-ins', { params: { client_id: clientId, type } })
      .then((res) => setRows(res.data))
      .catch(() => setRows([]))
      .finally(() => { setListLoading(false); setLoading(false); });
  }, [clientId, type]);

  const title = type === 'email' ? 'Opted Emails' : 'Opted Phones';
  const columnLabel = type === 'email' ? 'Email' : 'Phone';

  const allSelected = rows.length > 0 && selectedIds.size === rows.length;
  const someSelected = selectedIds.size > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(rows.map((r) => r.id)));
    }
  };

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const downloadSelected = () => {
    if (selectedIds.size === 0) return;
    const selected = rows.filter((r) => selectedIds.has(r.id));
    const header = type === 'email' ? ['email', 'date accepted'] : ['phone', 'date accepted'];
    const lines = [header.join(',')];
    selected.forEach((r) => {
      const value = r.value.replace(/"/g, '""');
      lines.push(`"${value}","${r.joined_date}"`);
    });
    const csv = lines.join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `opt-in-${type}s-selected-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="dashboard">
      <DashboardHeader title={title} showNav={false} showEmailButton={false} />
      <div className="dashboard-content">
        <div className="opted-list-section">
          <div className="detail-header">
            <button
              type="button"
              onClick={() => navigate(isAdminUser ? '/dashboard?tab=users' : '/dashboard')}
              className="back-button"
            >
              ← Back
            </button>
            <h1>{title}</h1>
          </div>

          <div className="opted-list-card">
            <div className="opted-client-select">
              <label htmlFor="opted-client">Company</label>
              <select
                id="opted-client"
                value={clientId ?? ''}
                onChange={(e) => {
                  setClientId(e.target.value === '' ? null : Number(e.target.value));
                  setSelectedIds(new Set());
                }}
              >
                <option value="">-- Select company --</option>
                {clientList.map((c) => (
                  <option key={c.id} value={c.id}>{c.client_name}</option>
                ))}
              </select>
            </div>

            <div className="opted-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={downloadSelected}
                disabled={!someSelected}
              >
                Download selected ({selectedIds.size})
              </button>
            </div>

            {listLoading ? (
              <div className="loading">Loading...</div>
            ) : (
              <div className="opted-table-wrap">
                <table className="opted-table">
                  <thead>
                    <tr>
                      <th className="col-checkbox">
                        {rows.length > 0 && (
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={toggleAll}
                            aria-label="Select all"
                          />
                        )}
                      </th>
                      <th>{columnLabel}</th>
                      <th>Joined date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="no-rows">No opted {type === 'email' ? 'emails' : 'phones'} for this company.</td>
                      </tr>
                    ) : (
                      rows.map((row) => (
                        <tr key={row.id}>
                          <td className="col-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedIds.has(row.id)}
                              onChange={() => toggleOne(row.id)}
                              aria-label={`Select ${row.value}`}
                            />
                          </td>
                          <td>{row.value}</td>
                          <td>{row.joined_date}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default OptedListPage;
